import { Icon } from '@aqarmap/aqarmap-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LocationType } from '~addListing/types';
import { useGetLocationsMutation } from '~/redux/api/userApiSlice';
import LocationsSkeleton from '~addListing/pages/InitializeStep/Location/LocationsSkeleton';
import SuggestLocation from '~addListing/pages/InitializeStep/Location/SuggestLocation';
import { useTranslation } from 'react-i18next';
import SubLocationSearch from '~addListing/pages/InitializeStep/Location/SubLocationSearch';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import LocationsItems from '~addListing/pages/InitializeStep/Location/LocationsItems';
import { LocationSearchType } from '.';

type LocationSearchProps = {
  parentLocation: LocationType | null;
  location: LocationType | null;
  selectedLocationIndex: number;
  handleSelectMainLocation: (
    location: LocationType | null,
    index: number,
  ) => void;
  handleResetLocation: () => void;
  errors: FieldErrors<LocationSearchType>;
  clearErrors: UseFormClearErrors<LocationSearchType>;
  setValue: UseFormSetValue<LocationSearchType>;
  allowSelectAllLocations?: boolean;
  hideSuggestLocation?: boolean;
};

const LocationSearch = ({
  parentLocation,
  location,
  selectedLocationIndex,
  handleSelectMainLocation,
  handleResetLocation,
  errors,
  clearErrors,
  setValue,
  allowSelectAllLocations,
  hideSuggestLocation,
}: LocationSearchProps) => {
  const { t } = useTranslation('app');
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [isError, setIsError] = useState(false);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState({
    keywordSearch: location?.title_full_path ?? '',
    limit: 100,
    locationParent: parentLocation?.id || 0,
  });
  const [getLocations, { isLoading }] = useGetLocationsMutation();

  const hasLocations = locations.length > 0;
  let showContent = hasLocations || isLoading;

  const getSearchQuery = () => {
    const filteredQuery = Object.fromEntries(
      Object.entries(searchQuery).filter(
        ([key, value]) =>
          !(key === 'locationParent' && value === 0) &&
          !(key === 'keywordSearch' && value === ''),
      ),
    );

    return new URLSearchParams(
      filteredQuery as Record<string, string>,
    ).toString();
  };

  const handleSelectLocation = (location: LocationType) => {
    handleSelectMainLocation(location, selectedLocationIndex);
    setSearchQuery((prev) => ({
      ...prev,
      keywordSearch: location.title_full_path,
      locationParent: location.id,
    }));
    setLocations([]);
  };

  const handleFetchLocations = useCallback(async () => {
    const data = await getLocations(getSearchQuery()).unwrap();
    setLocations(data);
  }, [searchQuery]);

  useEffect(() => {
    const isKeywordOrLocationMatch =
      searchQuery.keywordSearch === '' ||
      location?.title_full_path === searchQuery.keywordSearch;
    const hasSearchKeyword = searchQuery.keywordSearch !== '';

    if (isKeywordOrLocationMatch) {
      setLocations([]);
      if (!hasSearchKeyword) {
        setSearchQuery((prev) => ({
          ...prev,
          locationParent: parentLocation?.id || 0,
        }));
        handleSelectMainLocation(null, selectedLocationIndex);
        if (selectedLocationIndex === 0) handleResetLocation();
      }
    } else handleFetchLocations();
  }, [searchQuery.keywordSearch]);

  useEffect(() => {
    if (errors.location) setIsError(true);
    else setIsError(false);
  }, [errors]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target as Node) &&
        !isLoading
      ) {
        setLocations([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {(!location || selectedLocationIndex === 0) && (
        <div ref={dropdownMenuRef} className="relative select-none">
          <div
            className={`${showContent ? '!rounded-b-none' : ''} ${isError ? '!border-red_dark' : 'border-gray'}  w-full  flex gap-1.5x items-center border rounded-[12px] justify-between cursor-pointer border-b  px-1.5x`}
          >
            <Icon
              name="location"
              size="24px"
              className={
                selectedLocationIndex === 0
                  ? '!text-blue'
                  : location
                    ? '!text-gray__dark_1'
                    : '!text-blue'
              }
            />
            <input
              type="text"
              className="flex-1 h-[40px]"
              autoComplete="off"
              placeholder={t('search')}
              value={searchQuery.keywordSearch}
              onChange={(e) =>
                setSearchQuery((prev) => ({
                  ...prev,
                  keywordSearch: e.target.value,
                }))
              }
              onFocus={() => {
                if (selectedLocationIndex !== 0) handleFetchLocations();
                if (location === null) {
                  clearErrors('location');
                  setIsError(false);
                }
              }}
            />
            {searchQuery.keywordSearch && (
              <button
                onClick={() => {
                  setSearchQuery((prev) => ({
                    ...prev,
                    keywordSearch: '',
                  }));
                  setValue('location', null);
                }}
              >
                <Icon name="close" size="24px" className="text-gray__dark_2" />
              </button>
            )}
          </div>
          {showContent && (
            <div
              className={`content absolute top-full left-0 w-full ${hideSuggestLocation ? 'max-h-[550px]' : 'max-h-[400px]'} border border-gray border-t-0 bg-white rounded-b-[12px] z-10`}
            >
              {isLoading ? (
                <LocationsSkeleton />
              ) : (
                <>
                  <LocationsItems
                    handleSelectLocation={handleSelectLocation}
                    locations={locations}
                    allowSelectAllLocations={allowSelectAllLocations}
                    hideSuggestLocation={hideSuggestLocation}
                  />
                  {!hideSuggestLocation && <SuggestLocation />}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {selectedLocationIndex === 0 && location && (
        <>
          {location.title_full_path
            .split('/')
            .slice(0, -1)
            .map((location) => (
              <div
                key={location}
                className="flex gap-1.5x py-x px-1.5x h-[40px] border border-gray rounded-[12px] bg-gray_light_1"
              >
                <Icon
                  name="location"
                  size="24px"
                  className="text-gray__dark_1"
                />
                <p className="text-body_2 text-gray__dark_2">{location}</p>
              </div>
            ))}
        </>
      )}
      {isError && (
        <p className="text-red_dark text-body_2 !mb-0.5x">
          {t('requiredLocation')}
        </p>
      )}

      {location && location.parent && (
        <SubLocationSearch
          parentLocation={location}
          handleSelectLocation={handleSelectLocation}
          location={location}
          errors={errors}
          clearErrors={clearErrors}
          allowSelectAllLocations={allowSelectAllLocations}
          hideSuggestLocation={hideSuggestLocation}
        />
      )}
    </>
  );
};

export default LocationSearch;
